<template>
  <v-card class="elevation-12" :loading="loading">
    <v-toolbar
        color="primary"
        dark
        flat
    >
      <v-toolbar-title>{{ $t('editGlobalUnitForm.title') }}</v-toolbar-title>
    </v-toolbar>
    <v-spacer class="py-2"></v-spacer>
    <v-card-text>
      <v-form>
        <v-text-field
            v-model="form.name"
            :label="$t('addGlobalUnitForm.labels.name')"
            type="text"
            maxlength="400"
            :counter="400"
            :error-messages="nameErrors"
            @keyup="form.isUnitNameExists = false"
        />

        <v-text-field
            v-model="form.address"
            :label="$t('addGlobalUnitForm.labels.address')"
            type="text"
            maxlength="600"
            :counter="600"
            :error-messages="addressErrors"
        />

        <v-card class="mx-auto">
          <v-card-text>
            <div>{{ $t('addGlobalUnitForm.labels.regions') }}</div>
            <v-row>
              <v-col cols="12" md="12" lg="4" class="pa-0"
                     v-for="item in form.selectedRegions"
                     :key="item.region.id"
              >
                <v-checkbox
                    v-model="item.allowed"
                    class="mt-2 pa-2"
                    :label="item.region.name"
                    :hide-details="true"
                    :error-messages="regionErrors"
                    dense
                />
              </v-col>
            </v-row>
            <div v-if="!anyCheckboxSelected" class="error--text">
              {{ $t('addGlobalUnitForm.errors.regionRequired') }}
            </div>
          </v-card-text>
        </v-card>

        <v-radio-group v-if="isAdmin" v-model="form.isHead">
          <template v-slot:label>
            <strong>{{ $t('addUnitForm.unitType.title') }}</strong>
          </template>
          <v-radio :value="true" color="success">
            <template v-slot:label>
              <div>{{ $t('addUnitForm.unitType.head') }}</div>
            </template>
          </v-radio>
          <v-radio :value="false" color="secondary">
            <template v-slot:label>
              <div>{{ $t('addUnitForm.unitType.regular') }}</div>
            </template>
          </v-radio>
        </v-radio-group>

        <v-snackbar v-model="form.isUnitNameExists"
                    :timeout="5000"
                    :top="true"
                    color="error"
        >
          {{ $t('addGlobalUnitForm.messages.unitExists') }}
        </v-snackbar>

        <v-snackbar v-model="isServerError"
                    :timeout="5000"
                    :top="true"
                    color="error"
        >
          {{ $t('addGlobalUnitForm.messages.serverError') }}
        </v-snackbar>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-btn text color="secondary" @click="onCancelButtonClicked">{{ $t('addGlobalUnitForm.actions.cancel') }}</v-btn>
      <v-spacer/>
      <v-btn text color="primary" :disabled="!allowSave" @click="onSaveButtonClicked">{{ $t('editGlobalUnitForm.actions.save') }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import {maxLength, minLength, required} from "vuelidate/lib/validators";
import {LAYOUT_LOADING_START, LAYOUT_LOADING_STOP} from "@/store/modules/layout/actions";
import _ from "underscore";
import GlobalUnitService from "@/services/global.unit.service";
import {Admin} from "../../enums/user.type";

export default {
  name: "EditGlobalUnitComponent",
  props: {
    allowedRegions: {
      type: Array,
      default: () => {
        return []
      }
    }
  },
  data() {
    return {
      loading: false,
      unit: null,

      form: {
        id: '',
        name: '',
        isHead: false,
        address: '',
        organizationInn: '',
        selectedRegions: [],

        isUnitNameExists: false,
        isTryChangeHeadUnit: false
      },
      isServerError: false
    }
  },
  computed: {
    nameErrors() {
      const errors = []

      if (this.$v.form.name.$dirty) {
        if (!this.$v.form.name.required) {
          errors.push(this.$t('addGlobalUnitForm.errors.nameRequired'))
        } else {
          !this.$v.form.name.maxLength && errors.push(this.$t('addGlobalUnitForm.errors.nameTooLong'))
        }
      }

      return errors
    },
    isAdmin() {
      return this.$store.getters.appIsInitialized && this.$store.getters.appLogin?.type === Admin;
    },
    addressErrors() {
      const errors = []

      if (this.$v.form.address.$dirty) {
        if (!this.$v.form.address.required) {
          errors.push(this.$t('addGlobalUnitForm.errors.addressRequired'))
        } else {
          !this.$v.form.address.maxLength && errors.push(this.$t('addGlobalUnitForm.errors.addressTooLong'))
        }
      }

      return errors
    },
    regionErrors() {
      const errors = []

      if (this.$v.form.selectedRegions.$dirty) {
        if (!this.$v.form.selectedRegions.required) {
          errors.push(this.$t('addGlobalUnitForm.errors.regionRequired'))
        } else {
          !this.$v.form.selectedRegions.minLength && errors.push(this.$t('addGlobalUnitForm.errors.regionRequired'))
        }
      }

      return errors
    },
    anyCheckboxSelected() {
      return _.filter(this.form.selectedRegions, r => { return r.allowed }).length > 0
    },
    allowSave() {
      let allow = this.anyCheckboxSelected
      return allow
    }
  },
  validations: {
    form: {
      name: {
        required,
        maxLength: maxLength(400)
      },
      address: {
        required,
        maxLength: maxLength(600)
      },
      selectedRegions: {
        required,
        minLength: minLength(1)
      }
    }
  },
  methods: {
    set(unit) {
      this.clear()
      this.form.id = unit.id
      this.form.name = unit.name
      this.form.isHead = unit.isHead
      this.form.address = unit.address
      this.unit = unit

      this.form.selectedRegions = _.map(this.allowedRegions, region => {
        return {
          region: region,
          allowed: !!_.find(unit.regions, r => {
            return r.id === region.id
          })
        }
      })
    },
    clear() {
      this.$v.$reset()
      this.form.name = ''
      this.form.isHead = false
      this.form.address = ''
      this.unit = null
      this.form.selectedRegions = _.map(this.allowedRegions, region => {
        return {
          region: region,
          allowed: false
        }
      })
    },
    isCurrentUnitHead() {
      return this.unit.isHead;
    },
    onCancelButtonClicked() {
      this.clear()
      this.$emit('cancel')
    },
    onSaveButtonClicked() {
      this.$v.form.$touch()
      if (!this.$v.form.$invalid) {
        this.loading = true;
        this.$store.dispatch(LAYOUT_LOADING_START)

        const checkedRegions = _.filter(this.form.selectedRegions, ar => {
          return ar.allowed
        })

        this.isServerError = false
        GlobalUnitService.editUnit({
          id: this.form.id,
          unitName: this.form.name,
          organizationInn: this.unit.globalorganization.inn,
          isHead: this.form.isHead,
          address: this.form.address,
          region: _.map(checkedRegions, ar => {
            return ar.region
          })
        })
            .then(() => {
              this.clear()
              this.$emit('updated')
            }, () => {
              this.isServerError = true
            })
            .finally(() => {
              this.loading = false
              this.$store.dispatch(LAYOUT_LOADING_STOP)
            })
      }
    }
  },
  created() {
    this.clear()
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.load()
      }
    }
  }
}
</script>

<style scoped>

</style>